import { Repeat, X } from 'lucide-react';
import { Type } from '@/shared/schema';
import { Condition, LogicalOperator, OperatorType } from './nestedRuleFunctions';
import DropdownSelect from '@/components/uiCustomComps/DropdownSelect';
import { useAttributes } from '@/hooks/useLocalStorage';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface RuleConditionProps {
    condition: Condition;
    groupId: string;
    index: number;
    groupOperators: LogicalOperator[];
    isFirstInGroup: boolean;
    dataType: Type[];
    operators: OperatorType[];
    onUpdateCondition: (condId: string, field: string, value: any) => void;
    onUpdateDatatype: (condId: string, field: string, value: any) => void;
    onUpdateLogicalOperator: (groupId: string, index: number, operator: LogicalOperator) => void;
    onDeleteCondition: (groupId: string, condId: string) => void;
    version: boolean;
}

const RuleCondition = ({
    condition,
    groupId,
    index,
    groupOperators,
    isFirstInGroup,
    dataType,
    operators,
    onUpdateCondition,
    onUpdateDatatype,
    onUpdateLogicalOperator,
    onDeleteCondition,
    version
}: RuleConditionProps) => {
    const [dt1, setDt1] = useState<string>()
    const [dt2, setDt2] = useState<string>()
    const { data: attributes1 } = useAttributes(dt1 ?? "");
    const { data: attributes2 } = useAttributes(dt2 ?? "");

    const dataTypeOptions = dataType.map(dt => ({
        value: dt?.Name || '',
        label: dt?.Name || 'Unknown'
    }));

    const attributeOptions1 = (attributes1 || []).map(attr => ({
        value: attr?.Name || '',
        label: attr?.Name || 'Unknown'
    }));

    const attributeOptions2 = (attributes2 || []).map(attr => ({
        value: attr?.Name || '',
        label: attr?.Name || 'Unknown'
    }));

    const operatorOptions  = [
    { value: '==', label: 'Equals (==)' },
    { value: '!=', label: 'Not Equals (!=)' },
    { value: '>', label: 'Greater Than (>)' },
    { value: '<', label: 'Less Than (<)' },
    { value: '>=', label: 'Greater or Equal (>=)' },
    { value: '<=', label: 'Less or Equal (<=)' }
];

    const logicalOperatorOptions = [
        { value: 'AND', label: 'AND' },
        { value: 'OR', label: 'OR' }
    ];

    return (
        <div className="space-y-2">

            {(!isFirstInGroup && groupId !== "root") && (
                <div className="flex items-center gap-2 text-sm text-gray-600 ml-4">
                    <DropdownSelect
                        value={groupOperators[index - 1] || 'AND'}
                        onValueChange={(value) => onUpdateLogicalOperator(groupId, index - 1, value as LogicalOperator)}
                        options={logicalOperatorOptions}
                        triggerClassName="text-sm px-2 py-1 min-w-[80px]"
                    />
                </div>
            )}


            <div className="flex items-center gap-2 bg-white p-2 rounded border">
                <DropdownSelect
                    key={`attr-${condition.id}-${condition.dataType}`}
                    value={condition.dataType}
                    onValueChange={(value) => {
                        const selectedDt = dataType.find(x => x.Name === value);
                        onUpdateDatatype(condition.id, 'dataType', selectedDt);
                        setDt1(String(selectedDt?.ID))
                    }}
                    options={dataTypeOptions}
                    placeholder="Select DataType"
                    disabled={version}
                />

                <DropdownSelect
                    value={condition.attribute}
                    onValueChange={(value) => onUpdateCondition(condition.id, 'attribute', value)}
                    options={attributeOptions1}
                    placeholder={`${condition.dataType ? condition.attribute ? condition.attribute : attributeOptions1.length === 0 ? "No Attributes" : "Select Attribute" : "Select Attribute"}`}
                    disabled={version || !condition.dataType || (attributeOptions1.length === 0 &&  !condition.attribute)}
                />

                <DropdownSelect
                    value={condition.operator}
                    onValueChange={(value) => onUpdateCondition(condition.id, 'operator', value)}
                    options={operatorOptions}
                    triggerClassName="w-[195px]"
                    disabled={version}
                />

                {condition.valueType === 'operand' ? (
                    <>
                        <DropdownSelect
                            value={condition.dataType2 ?? ""}
                            onValueChange={(value) => {
                                const selectedDt = dataType.find(x => x.Name === value);
                                onUpdateDatatype(condition.id, 'dataType2', selectedDt);
                                setDt2(String(selectedDt?.ID))
                            }}
                            options={dataTypeOptions}
                            placeholder="Select Type"
                        />

                        <DropdownSelect
                            key={`attr2-${condition.id}-${condition.dataType2}`}
                            value={condition.attribute2 ?? ""}
                            onValueChange={(value) => onUpdateCondition(condition.id, 'attribute2', value)}
                            options={attributeOptions2}
                            placeholder={`${condition.dataType2 ? condition.attribute2 ? condition.attribute2 : attributeOptions2.length === 0 ? "No Attributes" : "Select Attribute" : "Select Attribute"}`}
                            disabled={!condition.dataType2 || (attributeOptions2.length === 0 && !condition.attribute2)}
                        />
                    </>
                ) : (
                    <Input
                        type="text"
                        value={condition.value || ''}
                        onChange={(e) => onUpdateCondition(condition.id, 'value', e.target.value)}
                        placeholder="Enter value"
                        className="border rounded px-3 py-2 w-[250px]"
                    />
                )}

                <Button
                    onClick={() => onUpdateCondition(
                        condition.id,
                        'valueType',
                        condition.valueType === 'operand' ? 'value' : 'operand'
                    )}
                    variant="outline"
                    className="p-2 hover:bg-gray-100 rounded"
                    title={condition.valueType === 'operand' ? 'Switch to value input' : 'Switch to operand'}
                >
                    <Repeat className="w-4 h-4" />
                </Button>

                <Button
                    onClick={() => onDeleteCondition(groupId, condition.id)}
                    variant="outline"
                    className="p-2 hover:bg-red-50 rounded text-red-500"
                >
                    <X className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};

export default RuleCondition;